import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useTerminalStore } from '@/store/terminalStore';
import { useGitStore } from '@/store/gitStore';
import { gitEngine } from '@/lib/git/engine';

interface TerminalProps {
  onCommand: ((command: string) => { success: boolean; message: string }) | undefined;
  expectedCommand: string | undefined;
  onCorrectCommand: ((command: string) => void) | undefined;
  /** Автоматически инициализировать Git репозиторий при монтировании */
  autoInit?: boolean;
  /** Ключ для принудительного сброса терминала при смене урока */
  resetKey?: string;
}

/** Git подкоманды для автозаполнения */
const GIT_SUBCOMMANDS = [
  'init',
  'status',
  'add',
  'commit',
  'log',
  'branch',
  'checkout',
  'switch',
  'merge',
  'reset',
  'revert',
  'rm',
  'mv',
  'remote',
  'fetch',
  'pull',
  'push',
  'stash',
  'config',
  'help',
  'show',
  'diff',
  'tag',
  'rebase',
  'cherry-pick',
  'blame',
  'grep',
  'bisect',
  'reflog',
];

/**
 * Браузерный терминал для выполнения Git команд
 * Интегрирован с GitEngine для симуляции Git
 */
export function Terminal({
  onCommand,
  expectedCommand,
  onCorrectCommand,
  autoInit = true,
  resetKey,
}: TerminalProps) {
  const [currentInput, setCurrentInput] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    history,
    addEntry,
    addToCommandHistory,
    commandHistory,
    historyIndex,
    setHistoryIndex,
    clearHistory,
  } = useTerminalStore();
  const { init, setRepoState, reset: resetRepo } = useGitStore();

  // Инициализация терминала и Git репозитория
  useEffect(() => {
    if (history.length === 0) {
      addEntry({ type: 'output', content: 'Welcome to Git Terminal!' });
      addEntry({ type: 'output', content: 'Type "help" for available commands.\n' });
    }

    // Автоматическая инициализация Git репозитория
    if (autoInit && !isInitialized) {
      gitEngine.initializeRepo();
      init();
      setIsInitialized(true);
      console.log('[Terminal] Git repository initialized automatically');
    }
  }, [autoInit, isInitialized, init, addEntry, history.length]);

  // Сброс терминала при изменении resetKey (переход на новый урок)
  useEffect(() => {
    if (resetKey) {
      console.log('[Terminal] Reset triggered for lesson:', resetKey);

      // Очищаем историю
      clearHistory();

      // Сбрасываем состояние ввода
      setCurrentInput('');

      // Реинициализируем Git репозиторий
      gitEngine.reset();
      gitEngine.initializeRepo();
      resetRepo();

      // Добавляем приветственное сообщение
      addEntry({ type: 'output', content: 'Welcome to Git Terminal!' });
      addEntry({ type: 'output', content: 'Type "help" for available commands.\n' });

      console.log('[Terminal] Terminal reset complete');
    }
  }, [resetKey, clearHistory, resetRepo, addEntry]);

  // Автоскролл к низу
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [history]);

  // Фокус на input при клике
  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  // Синхронизация состояния GitEngine с gitStore
  const syncStateToStore = useCallback(() => {
    const engineState = gitEngine.getState();

    // Полная синхронизация состояния
    setRepoState(engineState);
    console.log('[Terminal] State synced:', engineState);
  }, [setRepoState]);

  // Обработка ввода команды
  const handleSubmit = useCallback(
    (command: string) => {
      if (!command.trim()) return;

      // Добавляем команду в историю
      addEntry({ type: 'input', content: `$ ${command}` });
      addToCommandHistory(command);

      // Обрабатываем команду через GitEngine
      const result = gitEngine.execute(command);

      // Обрабатываем специальные команды (help, clear)
      if (command.trim().toLowerCase() === 'help') {
        addEntry({ type: 'output', content: result.output });
        return;
      }

      if (command.trim().toLowerCase() === 'clear') {
        clearHistory();
        return;
      }

      if (result.success) {
        if (result.output) {
          addEntry({ type: 'output', content: result.output });
        }

        // Синхронизируем состояние после успешной команды
        syncStateToStore();

        // Проверяем, соответствует ли команда ожидаемой
        if (expectedCommand && onCorrectCommand) {
          const normalizedInput = command.trim().toLowerCase();
          const normalizedExpected = expectedCommand.trim().toLowerCase();

          // Проверяем точное совпадение или по regex
          if (
            normalizedInput === normalizedExpected ||
            new RegExp(`^${normalizedExpected}$`).test(normalizedInput)
          ) {
            onCorrectCommand(command);
          }
        }

        // Если есть внешний обработчик, вызываем его
        if (onCommand) {
          const externalResult = onCommand(command);
          if (!externalResult.success) {
            addEntry({ type: 'error', content: `✗ ${externalResult.message}` });
            toast.error('Команда не принята', {
              description: externalResult.message,
            });
          }
        }
      } else {
        addEntry({ type: 'error', content: result.error ?? 'Unknown error' });
        toast.error('Ошибка выполнения команды', {
          description: result.error ?? 'Unknown error',
        });
      }

      setCurrentInput('');
    },
    [
      addEntry,
      addToCommandHistory,
      clearHistory,
      syncStateToStore,
      expectedCommand,
      onCorrectCommand,
      onCommand,
    ]
  );

  // Обработка клавиш
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit(currentInput);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex < commandHistory.length - 1 ? historyIndex + 1 : historyIndex;
        setHistoryIndex(newIndex);
        setCurrentInput(commandHistory[commandHistory.length - 1 - newIndex] ?? '');
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setCurrentInput(commandHistory[commandHistory.length - 1 - newIndex] ?? '');
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setCurrentInput('');
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      // Автозаполнение для git команд
      handleAutocomplete();
    } else if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault();
      clearHistory();
    }
  };

  // Автозаполнение команд
  const handleAutocomplete = () => {
    const input = currentInput.toLowerCase();

    // Приоритет: ожидаемая команда
    if (expectedCommand && expectedCommand.toLowerCase().startsWith(input) && input.length > 0) {
      setCurrentInput(expectedCommand);
      return;
    }

    if (input.startsWith('git ')) {
      const partial = input.slice(4);
      // Ищем совпадения среди подкоманд
      const matches = GIT_SUBCOMMANDS.filter((s) => s.startsWith(partial));
      if (matches.length === 1) {
        setCurrentInput(`git ${matches[0]}`);
      } else if (matches.length > 1) {
        // Если несколько совпадений, показываем первое
        setCurrentInput(`git ${matches[0]}`);
      }
    } else if (input.startsWith('git')) {
      setCurrentInput('git ');
    } else if (input === 'gi') {
      setCurrentInput('git ');
    } else if (input === 'cl') {
      setCurrentInput('clear');
    } else if (input === 'he') {
      setCurrentInput('help');
    }
  };

  // Вычисляем подсказку для автозаполнения
  const autocompleteSuggestion = useMemo(() => {
    if (!currentInput || currentInput.length === 0) return null;

    const input = currentInput.toLowerCase();

    // Приоритет: ожидаемая команда
    if (expectedCommand && expectedCommand.toLowerCase().startsWith(input)) {
      return expectedCommand.slice(input.length);
    }

    // Git подкоманды
    if (input.startsWith('git ')) {
      const partial = input.slice(4);
      if (partial.length === 0) return null;
      const match = GIT_SUBCOMMANDS.find((s) => s.startsWith(partial) && s !== partial);
      if (match) {
        return match.slice(partial.length);
      }
    }

    return null;
  }, [currentInput, expectedCommand]);

  return (
    <div className="terminal-container flex h-full flex-col" onClick={handleContainerClick}>
      {/* Terminal header */}
      <div className="terminal-header">
        <div className="terminal-dot red" />
        <div className="terminal-dot yellow" />
        <div className="terminal-dot green" />
        <span className="ml-2 text-xs text-slate-400">bash — git-terminal</span>
        {expectedCommand && (
          <span className="ml-auto text-xs text-git-orange/70">Expected: {expectedCommand}</span>
        )}
      </div>

      {/* Terminal content */}
      <div ref={containerRef} className="flex-1 cursor-text overflow-y-auto p-4 font-mono text-sm">
        {history.map((entry) => (
          <div
            key={entry.id}
            className={cn(
              'whitespace-pre-wrap',
              entry.type === 'input' && 'text-slate-300',
              entry.type === 'output' && 'text-slate-400',
              entry.type === 'error' && 'text-red-400',
              entry.type === 'success' && 'text-green-400'
            )}
          >
            {entry.content}
          </div>
        ))}

        {/* Current input line */}
        <div className="flex items-center text-slate-300">
          <span className="text-green-400">$</span>
          <div className="ml-2 flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={currentInput}
              onChange={(e) => setCurrentInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full bg-transparent outline-none"
              autoFocus
              spellCheck={false}
              placeholder={expectedCommand ? 'Type a command...' : ''}
            />
            {/* Autocomplete suggestion overlay */}
            {autocompleteSuggestion && (
              <span className="absolute left-0 top-0 text-slate-500 pointer-events-none whitespace-pre">
                <span className="invisible">{currentInput}</span>
                <span className="text-slate-600">{autocompleteSuggestion}</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
